// Live market price surface for any Palworld TCG card.
//
// `MarketPriceTag` renders one compact price chip. Hover (or tap on touch) opens
// a small panel with the blended market value, graded value, a 30-day history
// chart and eBay Partner Network buy / sold / graded links, so a collector can
// see what a card is worth without leaving the page they are on.
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "@tanstack/react-router";
import { HoverChart } from "@/components/HoverChart";
import { useCardPrice } from "@/lib/use-card-prices";
import { formatUsd, PRICE_SOURCES, type PriceRow } from "@/lib/prices-shared";
import { OFFICIAL_CARD_BY_CODE } from "@/lib/tcg-cards";
import { trackAffiliateClick } from "@/lib/affiliate-clicks";
import { goUrl } from "@/lib/outbound";
import { RatingStars } from "@/components/CardRating";

// Buy row order: the two purchase destinations first, then the research links.
const EBAY_IDS = ["ebay", "tcgplayer", "ebay-graded"] as const;
const EBAY_LABEL: Record<(typeof EBAY_IDS)[number], string> = {
  ebay: "Buy On Ebay",
  tcgplayer: "Buy On TCGplayer",
  "ebay-graded": "Graded slabs",
};

/** True when a card has no live marketplace quote or stored history yet. */
function isPendingMarket(row: PriceRow): boolean {
  return row.liveSources.length === 0 && row.recordedDays === 0 && row.confidence === "modelled";
}

/** Small pre-release / thin-market notice used across card price surfaces. */
function PendingMarketNotice({ label, compact = false, className = "" }: { label: string; compact?: boolean; className?: string }) {
  return (
    <div className={`rounded-lg border border-dashed border-amber-500/40 bg-amber-500/5 ${compact ? "p-2" : "p-3"} ${className}`}>
      <p className={`font-semibold text-amber-300 ${compact ? "text-[10px]" : "text-xs"}`}>Pre-release: Market Pricing Pending</p>
      <p className={`mt-0.5 text-muted-foreground ${compact ? "text-[10px]" : "text-[11px]"}`}>
        {label} has not reached English marketplaces yet, so no live asking or sold data is available. Check back after the release date, or browse the card page for scans and set details.
      </p>
    </div>
  );
}

/**
 * 30-day price history chart. `compact` is the in-popover size.
 *
 * Uses the shared `HoverChart` so a card page reads exactly like the price chart
 * and price index: gridded plot, value/date axes, listing-depth columns and a
 * crosshair tooltip instead of numbers stamped on the line.
 */
export function PriceHistoryChart({ row, compact = false }: { row: PriceRow; compact?: boolean }) {
  // Open on TCGplayer when it has a real history, but fall back to whichever
  // platform does have 2+ stored days — a card with only eBay snapshots must
  // never open on an empty panel.
  const [view, setView] = useState<"blended" | "ebay" | "tcgplayer">(() =>
    row.tcgHistory.length >= 2 ? "tcgplayer" : row.ebayHistory.length >= 2 ? "ebay" : "blended",
  );
  const points = view === "ebay" ? row.ebayHistory : view === "tcgplayer" ? row.tcgHistory : row.history;
  const soldPoints = view === "ebay" ? [] : row.tcgSoldHistory;
  const labels = [...new Set([...points.map((p) => p.day), ...soldPoints.map((p) => p.day)])].sort();
  const askByDay = new Map(points.map((p) => [p.day, p]));
  const soldByDay = new Map(soldPoints.map((p) => [p.day, p]));
  const vals = [...points.map((p) => p.value), ...soldPoints.map((p) => p.value)];
  const label = view === "ebay" ? "eBay" : view === "tcgplayer" ? "TCGplayer" : "Blended";
  if (vals.length < 2) {
    return (
      <figure className={compact ? "" : "mt-3"}>
        <div className="mb-1.5 flex gap-1">
          {(["tcgplayer", "ebay", "blended"] as const).map((id) => (
            <button key={id} type="button" onClick={() => setView(id)} aria-pressed={view === id} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${view === id ? "bg-primary/20 text-primary" : "border border-border/60 text-muted-foreground"}`}>
              {id === "tcgplayer" ? "TCGplayer" : id === "ebay" ? "eBay" : "Blended"}
            </button>
          ))}
        </div>
        {isPendingMarket(row) ? (
          <PendingMarketNotice label={`${row.code} ${row.name}`} compact={compact} />
        ) : (
          <p className="rounded-lg border border-dashed border-border/60 p-3 text-[10px] text-muted-foreground">
            No {label} history is available for this printing yet.
          </p>
        )}
      </figure>
    );
  }
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const up = row.change30d >= 0;
  const stroke = up ? "hsl(152 70% 50%)" : "hsl(0 75% 62%)";
  return (
    <figure className={compact ? "" : "mt-3"}>
      <div className="mb-1.5 flex gap-1">
        {(["tcgplayer", "ebay", "blended"] as const).map((id) => (
          <button key={id} type="button" onClick={() => setView(id)} aria-pressed={view === id} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${view === id ? "bg-primary/20 text-primary" : "border border-border/60 text-muted-foreground"}`}>
            {id === "tcgplayer" ? "TCGplayer" : id === "ebay" ? "eBay" : "Blended"}
          </button>
        ))}
      </div>
      <HoverChart
        labels={labels}
        series={[
          { id: `${row.code}-${view}`, label, color: stroke, values: labels.map((day) => askByDay.get(day)?.value) },
          ...(soldPoints.length
            ? [{ id: `${row.code}-sold`, label: "TCGplayer sold avg", color: "hsl(42 92% 58%)", values: labels.map((day) => soldByDay.get(day)?.value) }]
            : []),
        ]}
        area
        height={compact ? 150 : 260}
        baseline={view !== "ebay" && row.tcgSoldAvg && row.tcgSoldAvg > 0 ? row.tcgSoldAvg : undefined}
        format={(v) => formatUsd(v)}
        ariaLabel={`30-day Palworld card price history for ${row.name} ${row.code}`}
        columns={{ label: "Listings sampled", values: labels.map((day) => askByDay.get(day)?.listings), color: "hsl(210 90% 66%)" }}
        tooltipExtra={(i) => (
          <p className="whitespace-nowrap text-[11px] text-muted-foreground">
            {askByDay.get(labels[i])?.real ? `${label} snapshot` : "No asking snapshot"}
          </p>
        )}
      />
      <figcaption
        className={`flex flex-wrap justify-between gap-x-3 text-muted-foreground ${compact ? "text-[10px]" : "mt-1 text-[11px]"}`}
      >
        <span>30d low {formatUsd(min)}</span>
        <span>high {formatUsd(max)}</span>
        <span>
          {points.some((point) => point.real)
            ? `${points.filter((point) => point.real).length} ${label} snapshot${points.filter((point) => point.real).length === 1 ? "" : "s"}`
            : "index baseline"}
        </span>
      </figcaption>
    </figure>
  );
}

/**
 * Buy / research row.
 *
 * The Japanese / Chinese / Korean eBay links are always offered: they are live search
 * links, so a visitor can find an Asian-printing auction even on a card our
 * daily English feed never sampled. They are purchase options only — nothing
 * here feeds the English market value, the price chart or the price index.
 */
function EbayRow({ code, compact = false }: { code: string; compact?: boolean }) {
  const card = OFFICIAL_CARD_BY_CODE.get(code.toUpperCase());
  if (!card) return null;
  const ids: string[] = [...EBAY_IDS, "ebay-jp"];

  return (
    <div className={`flex flex-wrap gap-1.5 ${compact ? "" : "mt-3"}`}>
      {ids.map((id) => {
        const source = PRICE_SOURCES.find((s) => s.id === id);
        if (!source) return null;
        const primary = id === "ebay" || id === "tcgplayer" || id === "ebay-jp";
        return (
          <a
            key={id}
            href={primary ? goUrl(id, "card", card.code) : source.card(card)}
            target="_blank"
            rel="nofollow ugc noopener noreferrer"
            onClick={() => trackAffiliateClick({ source: id, kind: "card", targetKey: card.code })}
            title={source.note}
            className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
              id === "ebay"
                ? "btn-ebay"
                : id === "tcgplayer"
                  ? "btn-tcgplayer"
                  : id === "ebay-jp"
                    ? "btn-ebay"
                    : "border border-border/60 text-muted-foreground hover:border-primary/60 hover:text-foreground"
            }`}
          >
            {EBAY_LABEL[id as keyof typeof EBAY_LABEL] ?? "Buy JP/CN/KR"}
          </a>
        );
      })}
      <RatingStars code={card.code} label={`${card.code} ${card.name}`} className="ml-1" />
    </div>
  );
}


/** Full market panel used on card detail pages. */
export function CardMarketPanel({ code }: { code: string }) {
  const row = useCardPrice(code);
  return (
    <section className="glass mt-6 rounded-2xl p-6">
      <h2 className="text-xl font-bold">Market price &amp; 30-day history</h2>
      {row ? (
        <>
          {isPendingMarket(row) ? (
            <PendingMarketNotice label={`${row.code} ${row.name}`} className="mt-3" />
          ) : (
            <>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Market value" value={formatUsd(row.market)} />
                <Stat label="Graded (slabbed)" value={row.gradedAvg === null ? "—" : formatUsd(row.gradedAvg)} />
                <Stat
                  label="30-day change"
                  value={`${row.change30d >= 0 ? "+" : ""}${row.change30d.toFixed(1)}%`}
                  tone={row.change30d >= 0 ? "up" : "down"}
                />
                <Stat label="Live listings" value={row.listings ? String(row.listings) : "—"} />
              </div>
              <PriceHistoryChart row={row} />
              <EbayRow code={code} />
            </>
          )}
        </>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">Loading the latest marketplace quotes for {code}…</p>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        Values blend live eBay and TCGplayer asking prices with the transparent Pal Stars Index baseline.{" "}
        <Link to="/prices" search={{ card: code.toUpperCase() }} className="text-primary hover:underline">
          Open {code} on the Palworld TCG price chart →
        </Link>
      </p>
    </section>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "up" | "down" }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/40 p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={`mt-1 text-lg font-bold tabular-nums ${
          tone === "up" ? "text-emerald-400" : tone === "down" ? "text-destructive" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Compact price chip with a hover/tap market panel.
 *
 * The panel is rendered into a body-level portal at fixed coordinates. Inside a
 * card grid the tiles create their own stacking contexts (hover transforms), so
 * an in-tile absolute panel was painted *under* neighbouring tiles and its eBay
 * links could not be clicked. A portal also survives the tile's overflow.
 * Closing is delayed so the pointer can travel from the chip into the panel.
 */
export function MarketPriceTag({ code, className = "" }: { code: string; className?: string }) {
  const row = useCardPrice(code);
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number; above: boolean } | null>(null);
  const chip = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => setMounted(true), []);

  const clearTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    clearTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 220);
  };

  const place = useCallback(() => {
    const r = chip.current?.getBoundingClientRect();
    if (!r) return;
    const w = 280;
    const h = 250;
    const above = r.top > h + 12;
    const left = Math.min(Math.max(r.left + r.width / 2 - w / 2, 8), window.innerWidth - w - 8);
    setPos({ left, top: above ? r.top - 8 - h : r.bottom + 8, above });
  }, []);

  const show = () => {
    clearTimer();
    place();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!chip.current?.contains(t) && !panel.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onScroll = () => setOpen(false);
    document.addEventListener("pointerdown", onDocDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("pointerdown", onDocDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  useEffect(() => clearTimer, []);

  if (!row) return null;

  return (
    <span className={`inline-block ${className}`}>
      <button
        ref={chip}
        type="button"
        onMouseEnter={show}
        onMouseLeave={scheduleClose}
        onFocus={show}
        onClick={(e) => {
          e.preventDefault();
          if (open) setOpen(false);
          else show();
        }}
        aria-expanded={open}
        aria-label={`Palworld ${row.name} ${row.code} market price and eBay listings`}
        className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-semibold tabular-nums hover:border-emerald-400 ${isPendingMarket(row) ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"}`}
      >
        {isPendingMarket(row) ? "Pending" : formatUsd(row.market)}
        <span className="text-[9px] font-normal uppercase tracking-wide text-muted-foreground">{isPendingMarket(row) ? "Pre-release" : "eBay"}</span>
      </button>

      {mounted && open && pos
        ? createPortal(
            <div
              ref={panel}
              role="dialog"
              aria-label={`${row.name} ${row.code} market price`}
              onMouseEnter={clearTimer}
              onMouseLeave={scheduleClose}
              style={{ left: pos.left, top: pos.top, width: 280 }}
              className="fixed z-[130] rounded-xl border border-border/70 bg-background/95 p-3 text-left shadow-2xl backdrop-blur"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-xs font-semibold">
                  {row.name} <span className="font-mono text-[10px] text-muted-foreground">{row.code}</span>
                </span>
                <span
                  className={`text-[11px] font-semibold ${row.change30d >= 0 ? "text-emerald-400" : "text-destructive"}`}
                >
                  {row.change30d >= 0 ? "+" : ""}
                  {row.change30d.toFixed(1)}%
                </span>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-muted-foreground">
                <span>
                  Raw <strong className="text-foreground tabular-nums">{formatUsd(row.market)}</strong>
                </span>
                <span>
                  Graded{" "}
                  <strong className="text-foreground tabular-nums">
                    {row.gradedAvg === null ? "—" : formatUsd(row.gradedAvg)}
                  </strong>
                </span>
                <span>
                  Range {formatUsd(row.low)}–{formatUsd(row.high)}
                </span>
                {row.jpCount > 0 && row.jpAvg !== null ? (
                  <span>
                    JP / CN <strong className="text-amber-200 tabular-nums">{formatUsd(row.jpAvg)}</strong>
                  </span>
                ) : null}

              </div>
              <div className="mt-2">
                <PriceHistoryChart row={row} compact />
              </div>
              <EbayRow code={code} compact />
              <p className="mt-2 text-[10px] leading-snug text-muted-foreground">
                eBay links are affiliate links; Pal Stars may earn a commission.
              </p>
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
